import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getCompanySettings, updateCompanySettings } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { SearchableSelect } from '../components/ui/searchable-select';
import { toast } from 'sonner';
import { Save } from 'lucide-react';

const INDIAN_STATES = [
  { code: '01', name: 'Jammu & Kashmir' }, { code: '02', name: 'Himachal Pradesh' },
  { code: '03', name: 'Punjab' }, { code: '04', name: 'Chandigarh' },
  { code: '05', name: 'Uttarakhand' }, { code: '06', name: 'Haryana' },
  { code: '07', name: 'Delhi' }, { code: '08', name: 'Rajasthan' },
  { code: '09', name: 'Uttar Pradesh' }, { code: '10', name: 'Bihar' },
  { code: '11', name: 'Sikkim' }, { code: '12', name: 'Arunachal Pradesh' },
  { code: '13', name: 'Nagaland' }, { code: '14', name: 'Manipur' },
  { code: '15', name: 'Mizoram' }, { code: '16', name: 'Tripura' },
  { code: '17', name: 'Meghalaya' }, { code: '18', name: 'Assam' },
  { code: '19', name: 'West Bengal' }, { code: '20', name: 'Jharkhand' },
  { code: '21', name: 'Odisha' }, { code: '22', name: 'Chhattisgarh' },
  { code: '23', name: 'Madhya Pradesh' }, { code: '24', name: 'Gujarat' },
  { code: '25', name: 'Daman & Diu' }, { code: '26', name: 'Dadra & Nagar Haveli' },
  { code: '27', name: 'Maharashtra' }, { code: '29', name: 'Karnataka' },
  { code: '30', name: 'Goa' }, { code: '31', name: 'Lakshadweep' },
  { code: '32', name: 'Kerala' }, { code: '33', name: 'Tamil Nadu' },
  { code: '34', name: 'Puducherry' }, { code: '35', name: 'Andaman & Nicobar' },
  { code: '36', name: 'Telangana' }, { code: '37', name: 'Andhra Pradesh' },
  { code: '38', name: 'Ladakh' },
];

export { INDIAN_STATES };

const GST = () => {
  const { isAdmin } = useAuth();

  // Company Settings
  const [settings, setSettings] = useState({ company_name: '', gstin: '', state: '', state_code: '' });
  const [settingsSaving, setSettingsSaving] = useState(false);

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    try { const r = await getCompanySettings(); setSettings(r.data); }
    catch (e) { console.error(e); }
  };

  const handleSaveSettings = async () => {
    setSettingsSaving(true);
    try { await updateCompanySettings(settings); toast.success('Company settings saved'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    setSettingsSaving(false);
  };

  const handleStateChange = (stateName) => {
    const st = INDIAN_STATES.find(s => s.name === stateName);
    setSettings(prev => ({ ...prev, state: stateName, state_code: st?.code || '' }));
  };

  return (
    <div className="space-y-4" data-testid="gst-page">
      <div className="flex items-center justify-between">
        <h1 className="text-lg sm:text-xl font-bold">GST Settings</h1>
      </div>

      <div className="max-w-xl space-y-4 p-4 border rounded-lg">
        <div className="space-y-2"><Label>Company Name</Label><Input value={settings.company_name} onChange={(e) => setSettings(s => ({...s, company_name: e.target.value}))} placeholder="Your Company Name" data-testid="company-name-input" /></div>
        <div className="space-y-2"><Label>GSTIN</Label><Input value={settings.gstin} onChange={(e) => setSettings(s => ({...s, gstin: e.target.value.toUpperCase()}))} placeholder="e.g. 24ABCDE1234F1Z5" maxLength={15} className="font-mono uppercase" data-testid="company-gstin-input" /></div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2"><Label>State</Label><SearchableSelect value={settings.state} onChange={handleStateChange} options={INDIAN_STATES.map(s => ({ value: s.name, label: `${s.code} - ${s.name}` }))} placeholder="Select state" data-testid="company-state-select" /></div>
          <div className="space-y-2"><Label>State Code</Label><Input value={settings.state_code} readOnly className="bg-muted font-mono" data-testid="company-state-code" /></div>
        </div>
        {isAdmin && <Button onClick={handleSaveSettings} disabled={settingsSaving} data-testid="save-settings-btn"><Save className="h-4 w-4 mr-1" />{settingsSaving ? 'Saving...' : 'Save Settings'}</Button>}
      </div>
    </div>
  );
};

export default GST;
